import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker, LocalizationProvider, type PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import {
  useGetAttractionFilterOptions,
  useGetAttractionPerformance,
  useGetAttractionPerformanceSummary,
} from '~/hooks/apis/attractions';

import { Dayjs, getDayJs } from '~/services/TimeService';

import type {
  AttractionPerformanceParams,
  AttractionPerformanceRow,
  AttractionPerformanceSummary,
} from '~/types/attractionTypes';
import { formatAttractionType } from '~/types/attractionTypes';

type SortField =
  | 'popularityRank'
  | 'offerImpressions'
  | 'offerClicks'
  | 'name'
  | 'bookings'
  | 'ttvAr'
  | 'marginPct'
  | 'offerCount';

const IMG_PLACEHOLDER = (
  <Box
    sx={{
      width: 56,
      height: 42,
      borderRadius: 1,
      bgcolor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
      No img
    </Typography>
  </Box>
);

function PerformanceImage({ src, alt }: { src?: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return IMG_PLACEHOLDER;
  const url = src.startsWith('http')
    ? src
    : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_112,h_84/${src}`;
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
      sx={{
        width: 56,
        height: 42,
        objectFit: src.includes('wikimedia') ? 'contain' : 'cover',
        backgroundColor: src.includes('wikimedia') ? '#f5f5f5' : undefined,
        borderRadius: 1,
      }}
    />
  );
}
type SortDirection = 'asc' | 'desc';

const DATE_SHORTCUTS: Array<PickersShortcutsItem<DateRange<Dayjs>>> = [
  { label: 'Last 7 Days', getValue: () => [getDayJs().subtract(7, 'day'), getDayJs().subtract(1, 'day')] },
  { label: 'Last 14 Days', getValue: () => [getDayJs().subtract(14, 'day'), getDayJs().subtract(1, 'day')] },
  { label: 'Last 28 Days', getValue: () => [getDayJs().subtract(28, 'day'), getDayJs().subtract(1, 'day')] },
];
const COUNTRY_SCOPED_CAROUSEL_TILE_WARNING = 'carousel_tile_metrics_country_scoped';
const ATTRIBUTION_ONLY_FINANCIALS_WARNING = 'financial_metrics_attribution_only';
// The admin dark theme currently leaves Autocomplete portal options too low-contrast without local paper/listbox overrides.
const FILTER_AUTOCOMPLETE_SLOT_PROPS = {
  paper: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      '& .MuiAutocomplete-listbox': {
        bgcolor: 'background.paper',
        color: 'text.primary',
      },
      '& .MuiAutocomplete-option': {
        color: 'text.primary',
        '&[aria-selected="true"]': {
          bgcolor: 'action.selected',
        },
        '&[aria-selected="true"].Mui-focused': {
          bgcolor: 'action.selected',
        },
        '&.Mui-focused': {
          bgcolor: 'action.hover',
        },
      },
    },
  },
};
const COUNTRY_QUERY_VALUES: Record<string, string> = {
  AU: 'AU',
  Australia: 'AU',
  NZ: 'NZ',
  'New Zealand': 'NZ',
  US: 'US',
  'United States': 'US',
  GB: 'GB',
  'United Kingdom': 'GB',
  SG: 'SG',
  Singapore: 'SG',
  MY: 'MY',
  Malaysia: 'MY',
  TH: 'TH',
  Thailand: 'TH',
  JP: 'JP',
  Japan: 'JP',
  ID: 'ID',
  Indonesia: 'ID',
  IN: 'IN',
  India: 'IN',
};

function getInitialFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const countryParam = params.get('country');

  return {
    country: countryParam ? (COUNTRY_QUERY_VALUES[countryParam] ?? 'AU') : 'AU',
    state: params.get('state') ?? '',
    province: params.get('province') ?? '',
    city: params.get('city') ?? '',
    attractionType: params.get('type') ?? '',
    searchName: params.get('name') ?? '',
  };
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-AU');
}

function formatCurrency(n: number | undefined): string {
  if (n === undefined || n === 0) return '--';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function formatMarginPct(n: number | undefined): string {
  if (n === undefined || n === 0) return '--';
  return `${(n * 100).toFixed(1)}%`;
}

function safeDivide(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0;
  }
  return numerator / denominator;
}

function isAttributionOnlyRow(row: AttractionPerformanceRow): boolean {
  const hasAttributedFinancials =
    (row.bookings ?? 0) > 0 ||
    (row.ttvAr ?? 0) > 0 ||
    (row.revenue ?? 0) > 0;
  const hasOfferListEngagement =
    row.offerImpressions > 0 ||
    row.offerClicks > 0;

  return hasAttributedFinancials && !hasOfferListEngagement;
}

interface FunnelStep {
  label: string;
  value: number;
  rate: number | null;
  scopeLabel?: string;
}

export function buildFunnelSteps(summary?: AttractionPerformanceSummary): Array<FunnelStep> {
  const carouselImpressions = summary?.carouselImpressions ?? 0;
  const tileClicks = summary?.tileClicks ?? 0;
  const offerClicks = summary?.offerClicks ?? 0;
  const bookings = summary?.bookings ?? 0;
  const hasCountryScopedCarouselTile =
    summary?.scope?.warnings?.includes(COUNTRY_SCOPED_CAROUSEL_TILE_WARNING) ?? false;
  const metricScopes = summary?.scope?.metricScopes;
  const canCompareOfferClicksWithTileClicks = !metricScopes || metricScopes.offerClicks === metricScopes.tileClicks;
  // Fine-filtered booking attribution can be disjoint from offer engagement, even when both are attraction-scoped.
  const canCompareBookingsWithOfferClicks =
    !metricScopes || (!hasCountryScopedCarouselTile && metricScopes.bookings === metricScopes.offerClicks);

  return [
    {
      label: 'Carousel Impressions',
      value: carouselImpressions,
      rate: null,
      ...(hasCountryScopedCarouselTile ? { scopeLabel: 'Country scope' } : {}),
    },
    {
      label: 'Tile Clicked',
      value: tileClicks,
      rate: safeDivide(tileClicks, carouselImpressions),
      ...(hasCountryScopedCarouselTile ? { scopeLabel: 'Country scope' } : {}),
    },
    {
      label: 'Offer Clicked',
      value: offerClicks,
      rate: canCompareOfferClicksWithTileClicks ? safeDivide(offerClicks, tileClicks) : null,
    },
    {
      label: 'Booked',
      value: bookings,
      rate: canCompareBookingsWithOfferClicks ? safeDivide(bookings, offerClicks) : null,
    },
  ];
}

function ConversionFunnel({ steps, loading }: { steps: Array<FunnelStep>; loading: boolean }) {
  const theme = useTheme();
  if (loading) {
    return <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 1 }} />;
  }

  const maxValue = Math.max(...steps.map((s) => s.value), 1);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, display: 'block' }}>
        Conversion Funnel
      </Typography>
      <Stack spacing={1}>
        {steps.map((step, i) => (
          <Box key={step.label}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Stack sx={{ minWidth: 130 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {step.label}
                </Typography>
                {step.scopeLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', lineHeight: 1.1 }}>
                    {step.scopeLabel}
                  </Typography>
                )}
              </Stack>
              <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.max((step.value / maxValue) * 100, 1)}%`,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 0.5,
                    opacity: 1 - i * 0.15,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 70, textAlign: 'right' }}>
                {formatNumber(step.value)}
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography
                variant="caption"
                color={step.rate !== null && step.rate > 0.1 ? 'success.main' : 'text.secondary'}
                sx={{ minWidth: 50, textAlign: 'right', fontWeight: 600 }}
              >
                {step.rate !== null ? formatPercent(step.rate) : '--'}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  cultural: { bg: '#7C3AED', text: '#fff' },
  natural: { bg: '#059669', text: '#fff' },
  entertainment: { bg: '#DB2777', text: '#fff' },
  religious: { bg: '#D97706', text: '#fff' },
  historical: { bg: '#1A56DB', text: '#fff' },
};

function AttractionTypeChip({ type }: { type: string }) {
  const colors = TYPE_COLORS[type] ?? { bg: '#6B7280', text: '#fff' };
  return (
    <Chip
      label={formatAttractionType(type)}
      size="small"
      sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
}

export default function AttractionPerformancePage() {
  const initialFilters = getInitialFiltersFromUrl();
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    getDayJs().subtract(7, 'day'),
    getDayJs().subtract(1, 'day'),
  ]);
  const [country, setCountry] = useState(initialFilters.country);
  const [state, setState] = useState(initialFilters.state);
  const [province, setProvince] = useState(initialFilters.province);
  const [city, setCity] = useState(initialFilters.city);
  const [attractionType, setAttractionType] = useState(initialFilters.attractionType);
  const [searchInput, setSearchInput] = useState(initialFilters.searchName);
  const [searchName, setSearchName] = useState(initialFilters.searchName);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<SortField>('popularityRank');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchName(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const hasCompleteDateRange = !!dateRange[0] && !!dateRange[1];

  const { data: filterOptionsData, error: filterOptionsError } = useGetAttractionFilterOptions({
    countryCode: country || undefined,
    state: state || undefined,
    province: province || undefined,
    activeOnly: true,
  });
  const filterOptions = filterOptionsData?.result;

  const summaryParams = useMemo<AttractionPerformanceParams>(
    () => ({
      from: dateRange[0]?.format('YYYY-MM-DD'),
      to: dateRange[1]?.format('YYYY-MM-DD'),
      countryCode: country || undefined,
      state: state || undefined,
      province: province || undefined,
      city: city || undefined,
      type: attractionType || undefined,
      name: searchName || undefined,
      activeOnly: true,
    }),
    [dateRange, country, state, province, city, attractionType, searchName],
  );

  const tableParams = useMemo<AttractionPerformanceParams>(
    () => ({
      ...summaryParams,
      sort: sortField,
      sortDirection: sortDir,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    [summaryParams, sortField, sortDir, page, rowsPerPage],
  );

  const summaryQuery = useGetAttractionPerformanceSummary(summaryParams, { enabled: hasCompleteDateRange });
  const tableQuery = useGetAttractionPerformance(tableParams, { enabled: hasCompleteDateRange });

  const summary = summaryQuery.data?.result;
  const total = tableQuery.data?.total ?? 0;
  const loading = summaryQuery.isLoading || tableQuery.isLoading;
  const error = summaryQuery.error || tableQuery.error || filterOptionsError;
  const hasCountryScopedCarouselTile =
    summary?.scope?.warnings?.includes(COUNTRY_SCOPED_CAROUSEL_TILE_WARNING) ?? false;
  const hasAttributionOnlyFinancials = summary?.scope?.warnings?.includes(ATTRIBUTION_ONLY_FINANCIALS_WARNING) ?? false;

  const handleSort = useCallback(
    (field: SortField) => {
      setSortDir((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
      setSortField(field);
      setPage(0);
    },
    [sortField],
  );

  const handleRowsPerPageChange = useCallback((e: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  }, []);

  const rows = useMemo<Array<AttractionPerformanceRow>>(() => tableQuery.data?.result ?? [], [tableQuery.data?.result]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
                slotProps={{
                  shortcuts: { items: DATE_SHORTCUTS },
                  textField: { size: 'small', sx: { width: 150 } },
                }}
              />
            </LocalizationProvider>

            <Select
              size="small"
              displayEmpty
              value={country}
              onChange={(e: SelectChangeEvent) => {
                setCountry(e.target.value);
                setState('');
                setProvince('');
                setCity('');
                setAttractionType('');
                setPage(0);
              }}
              sx={{ flex: 1, minWidth: 120 }}
            >
              <MenuItem value="">All Countries</MenuItem>
              <MenuItem value="AU">Australia</MenuItem>
              <MenuItem value="NZ">New Zealand</MenuItem>
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="GB">United Kingdom</MenuItem>
              <MenuItem value="SG">Singapore</MenuItem>
              <MenuItem value="MY">Malaysia</MenuItem>
              <MenuItem value="TH">Thailand</MenuItem>
              <MenuItem value="JP">Japan</MenuItem>
              <MenuItem value="ID">Indonesia</MenuItem>
              <MenuItem value="IN">India</MenuItem>
            </Select>

            <Autocomplete
              size="small"
              options={filterOptions?.states ?? []}
              value={state && (filterOptions?.states ?? []).includes(state) ? state : null}
              onChange={(_, value) => {
                setState(value || '');
                setProvince('');
                setCity('');
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="State" />}
              slotProps={FILTER_AUTOCOMPLETE_SLOT_PROPS}
              sx={{ flex: 1, minWidth: 140 }}
              disabled={!country}
            />

            <Autocomplete
              size="small"
              options={filterOptions?.provinces ?? []}
              value={province && (filterOptions?.provinces ?? []).includes(province) ? province : null}
              onChange={(_, value) => {
                setProvince(value || '');
                setCity('');
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Province" />}
              slotProps={FILTER_AUTOCOMPLETE_SLOT_PROPS}
              sx={{ flex: 1, minWidth: 140 }}
              disabled={!country}
            />

            <Autocomplete
              size="small"
              options={filterOptions?.cities ?? []}
              value={city && (filterOptions?.cities ?? []).includes(city) ? city : null}
              onChange={(_, value) => {
                setCity(value || '');
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="City" />}
              slotProps={FILTER_AUTOCOMPLETE_SLOT_PROPS}
              sx={{ flex: 1, minWidth: 140 }}
              disabled={!country}
            />

            <Select
              size="small"
              displayEmpty
              value={attractionType}
              onChange={(e: SelectChangeEvent) => {
                setAttractionType(e.target.value);
                setPage(0);
              }}
              sx={{ flex: 1, minWidth: 120 }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="cultural">Cultural</MenuItem>
              <MenuItem value="natural">Natural</MenuItem>
              <MenuItem value="entertainment">Entertainment</MenuItem>
              <MenuItem value="historical">Historical</MenuItem>
              <MenuItem value="religious">Religious</MenuItem>
            </Select>
          </Stack>
          <TextField
            size="small"
            fullWidth
            placeholder="Search name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error">Failed to load performance data. {error.message}</Alert>}

      {(hasCountryScopedCarouselTile || hasAttributionOnlyFinancials) && (
        <Alert severity="info">
          {hasCountryScopedCarouselTile &&
            'Carousel and tile metrics are country-level. Offer and booking metrics follow the selected attraction filters. '}
          {hasAttributionOnlyFinancials &&
            'Financial metrics are attribution-based and may lag after tracking changes.'}
        </Alert>
      )}

      <ConversionFunnel loading={loading} steps={buildFunnelSteps(summary)} />

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: 1,
                    px: 1,
                    whiteSpace: 'nowrap',
                    color: 'white',
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                <TableCell>Image</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Offers</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'offerImpressions'}
                    direction={sortField === 'offerImpressions' ? sortDir : 'desc'}
                    onClick={() => handleSort('offerImpressions')}
                  >
                    Offer Impressions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'offerClicks'}
                    direction={sortField === 'offerClicks' ? sortDir : 'desc'}
                    onClick={() => handleSort('offerClicks')}
                  >
                    Offer Clicks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'bookings'}
                    direction={sortField === 'bookings' ? sortDir : 'desc'}
                    onClick={() => handleSort('bookings')}
                  >
                    <Tooltip title="Attributed bookings captured from attraction-linked customer journeys.">
                      <span>Bookings</span>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'ttvAr'}
                    direction={sortField === 'ttvAr' ? sortDir : 'desc'}
                    onClick={() => handleSort('ttvAr')}
                  >
                    <Tooltip title="Attributed TTV captured from attraction-linked customer journeys.">
                      <span>TTV</span>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'marginPct'}
                    direction={sortField === 'marginPct' ? sortDir : 'desc'}
                    onClick={() => handleSort('marginPct')}
                  >
                    <Tooltip title="Attributed margin percentage. Values can be empty when attribution or margin data is unavailable.">
                      <span>Margin %</span>
                    </Tooltip>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No performance data available for the selected filters and date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const isAttributionOnly = isAttributionOnlyRow(row);

                  return (
                    <TableRow key={row.attractionId} hover>
                      <TableCell sx={{ width: 56 }}>
                        <PerformanceImage src={row.imageUrl} alt={row.name} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {row.attractionId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {[row.countryCode, row.state, row.city].filter(Boolean).join(' / ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <AttractionTypeChip type={row.type} />
                      </TableCell>
                      <TableCell align="right">{row.offerCount}</TableCell>
                      <TableCell align="right">{formatNumber(row.offerImpressions)}</TableCell>
                      <TableCell align="right">{formatNumber(row.offerClicks)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <span>{(row.bookings ?? 0) > 0 ? formatNumber(row.bookings ?? 0) : '--'}</span>
                          {isAttributionOnly && (
                            <Tooltip title="Attributed booking without offer-list clicks in this date range. Attribution can come from attraction tile interactions, not only offer clicks.">
                              <Chip
                                label="Attributed"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.625rem' }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.ttvAr ?? 0)}</TableCell>
                      <TableCell align="right">{formatMarginPct(row.marginPct ?? 0)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Pagination
            count={Math.ceil(total / rowsPerPage)}
            page={page + 1}
            onChange={(_, p) => setPage(p - 1)}
            color="primary"
            size="small"
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" color="text.disabled">
              {total} attractions
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Per page:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
              >
                {[10, 25, 50, 100].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
